import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Select } from '../../../components'
import { useMessage } from '../../../hooks'
import { eventService } from '../services'
import { appName } from '../appConfig'
import { EventListItem } from '../components'
import '../index.css'

const TIMEFRAME_OPTIONS = [
  { value: 14,  label: '2 veckor' },
  { value: 30,  label: '1 månad' },
  { value: 90,  label: '3 månader' },
  { value: 180, label: '6 månader' },
]

// An event with no end date is treated as ending when it starts — same
// convention as the calendar's effectiveEnd.
const effectiveEnd = (event) => event.event_end_timestamp
  ? new Date(event.event_end_timestamp)
  : new Date(event.event_start_timestamp)

const EventList = () => {
  const navigate = useNavigate()
  const { showMessage } = useMessage()
  const [daysAhead, setDaysAhead] = useState(90)
  const [allEvents, setAllEvents] = useState([])
  const [loading, setLoading]     = useState(true)

  // Fetched once, unbounded — every event that exists, regardless of date.
  // The timeframe Select below only narrows how far ahead "Kommande" looks;
  // it never hides anything from "Pågående" or "Tidigare".
  useEffect(() => {
    const ctrl = new AbortController()
    eventService.getAllUnbounded(ctrl.signal)
      .then(res => { setAllEvents(res.events || []); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda events', 'error'); setLoading(false) } })
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleInterest = (slug) => {
    return eventService.toggleInterest(slug).then(res => {
      setAllEvents(prev => prev.map(e => e.event_slug === slug
        ? { ...e, interest_count: res.interest_count, user_has_marked_interest: res.user_has_marked_interest, interested_users: res.interested_users }
        : e
      ))
      return res
    })
  }

  const now = new Date()
  const rangeEnd = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000)

  const ongoing = allEvents
    .filter(e => new Date(e.event_start_timestamp) <= now && effectiveEnd(e) >= now)
    .sort((a, b) => new Date(a.event_start_timestamp) - new Date(b.event_start_timestamp))

  const upcoming = allEvents
    .filter(e => new Date(e.event_start_timestamp) > now && new Date(e.event_start_timestamp) <= rangeEnd)
    .sort((a, b) => new Date(a.event_start_timestamp) - new Date(b.event_start_timestamp))

  // Most-recent-first — mirrors kayentaStories' StoryList.js ordering for its "presented" bucket.
  const past = allEvents
    .filter(e => effectiveEnd(e) < now)
    .sort((a, b) => new Date(b.event_start_timestamp) - new Date(a.event_start_timestamp))

  const renderSection = (title, list, emptyMessage) => (
    <div className="ev-list-section">
      <div className="ev-list-section-label">{title}</div>
      {list.length === 0
        ? <p style={{ color: 'var(--color-dark-grey)' }}>{emptyMessage}</p>
        : (
          <div className="ev-list">
            {list.map(event => (
              <EventListItem key={event.event_slug} event={event} onToggleInterest={toggleInterest} />
            ))}
          </div>
        )
      }
    </div>
  )

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <h1>Events</h1>
        <div className="ev-page-header-actions">
          <Select
            className="ev-timeframe-select"
            name="daysAhead"
            value={daysAhead}
            options={TIMEFRAME_OPTIONS}
            onChange={({ value }) => setDaysAhead(parseInt(value, 10))}
            searchTerm="" /* keeps our own weeks-then-months order — Select
                             otherwise re-sorts options alphabetically by label */
          />
          <Button value="+ nytt event" className="button main-button" onClick={() => navigate(`/${appName}/new`)} />
        </div>
      </div>

      {loading
        ? <p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p>
        : (
          <>
            {ongoing.length > 0 && renderSection('Pågående', ongoing, '')}
            {renderSection('Kommande', upcoming, 'Inga kommande events inom vald tidsram.')}
            {renderSection('Tidigare', past, 'Inga tidigare events.')}
          </>
        )
      }
    </div>
  )
}

export default EventList
